/**
 * Toast 消息提示组件
 *
 * 解决批量操作时右上角 Toast 刷屏的问题：
 * 1. 同内容合并去重：相同 type+message 的消息合并为一条，显示 ×N 计数并刷新倒计时
 * 2. 并发上限：同屏最多显示 MAX_VISIBLE 条，其余进入队列按序补位
 */
import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';
import styles from './Toast.module.css';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastMessage {
  id: string;          // 唯一 id（React key / 关闭定位）
  key: string;         // 合并键 type:message
  type: ToastType;
  message: string;
  count: number;       // 合并计数（>1 时显示 ×N）
  refreshKey: number;  // 合并时 +1，用于重置倒计时
  duration?: number;
}

interface ToastContextValue {
  showToast: (type: ToastType, message: string, duration?: number) => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

// 同屏最多显示的条数，其余排队
const MAX_VISIBLE = 3;

// 稳定自增 id 生成器（避免 Date.now() 碰撞）
let idCounter = 0;
const genId = (): string => `${Date.now()}-${idCounter++}`;

export function useToast() {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

interface ToastState {
  active: ToastMessage[];
  queue: ToastMessage[];
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ToastState>({ active: [], queue: [] });

  const showToast = useCallback((type: ToastType, message: string, duration = 4000) => {
    const mergeKey = `${type}:${message}`;

    setState(prev => {
      const { active, queue } = prev;

      // 1. 正在显示的同内容消息 → 合并计数 + 刷新倒计时
      const activeIdx = active.findIndex(t => t.key === mergeKey);
      if (activeIdx !== -1) {
        const newActive = active.map((t, i) =>
          i === activeIdx
            ? { ...t, count: t.count + 1, refreshKey: t.refreshKey + 1 }
            : t
        );
        return { active: newActive, queue };
      }

      // 2. 队列中已有同内容消息 → 合并计数（等补位时一并展示）
      const queueIdx = queue.findIndex(t => t.key === mergeKey);
      if (queueIdx !== -1) {
        const newQueue = queue.map((t, i) =>
          i === queueIdx ? { ...t, count: t.count + 1 } : t
        );
        return { active, queue: newQueue };
      }

      // 3. 全新消息：有空位直接显示，否则排队
      const newToast: ToastMessage = {
        id: genId(),
        key: mergeKey,
        type,
        message,
        count: 1,
        refreshKey: 0,
        duration,
      };
      if (active.length < MAX_VISIBLE) {
        return { active: [...active, newToast], queue };
      }
      return { active, queue: [...queue, newToast] };
    });
  }, []);

  const removeToast = useCallback((id: string) => {
    setState(prev => {
      const { active, queue } = prev;
      const removed = active.filter(t => t.id !== id);

      // 确实移除了一条，且队列有等待项 → 按序补位
      if (removed.length !== active.length && queue.length > 0) {
        const [next, ...rest] = queue;
        return { active: [...removed, next], queue: rest };
      }
      return { active: removed, queue };
    });
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className={styles.container}>
        <AnimatePresence>
          {state.active.map(toast => (
            <ToastItem key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({ toast, onClose }: { toast: ToastMessage; onClose: () => void }) {
  // 倒计时依赖 duration 与 refreshKey：同内容合并时 refreshKey+1 → 倒计时重置
  useEffect(() => {
    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(onClose, toast.duration);
      return () => clearTimeout(timer);
    }
  }, [toast.duration, toast.refreshKey, onClose]);

  const icons = {
    success: <CheckCircle size={20} />,
    error: <XCircle size={20} />,
    warning: <AlertCircle size={20} />,
    info: <Info size={20} />,
  };

  return (
    <motion.div
      className={`${styles.toast} ${styles[toast.type]}`}
      initial={{ opacity: 0, y: -20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.95 }}
      transition={{ duration: 0.2 }}
    >
      <span className={styles.icon}>{icons[toast.type]}</span>
      <span className={styles.message}>{toast.message}</span>
      {toast.count > 1 && (
        <span className={styles.countBadge}>×{toast.count}</span>
      )}
      <button className={styles.closeBtn} onClick={onClose}>
        <X size={16} />
      </button>
    </motion.div>
  );
}
